import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import ChatBubbleDisclosure from '../ChatBubbleDisclosure';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

/**
 * Sentinel value for the "Something else" free-text escape option in the
 * single-select group. Kept distinct from any real topic label so a typed
 * topic can never collide with it.
 */
const SOMETHING_ELSE = '__something_else__';

/**
 * The shared "selected" surface, mirroring ClarificationCard's idiom: a
 * primary-tinted background + thicker primary border on the chosen option, so
 * the selection reads consistently and stays distinct from the solid Confirm
 * button.
 */
const SELECTED_SURFACE_SX = {
  bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.12),
  border: '2px solid',
  borderColor: 'primary.main',
} as const;

interface Props {
  /** The clarifying question shown as a heading above the topic options. */
  question: string;
  /**
   * Candidate focus topics the readers discussed — rendered as single-select
   * (radio) options. The user resolves to exactly one focus.
   */
  topics: string[];
  /** The user's original query (kept on the props contract for parity / context). */
  originalQuery: string;
  /**
   * Resolve the focus ONCE:
   *   - a topic radio        → that topic's text
   *   - "Something else" + typed text → the typed text
   *   - Skip                 → `""` (answer everything, unfocused)
   */
  onChoose: (topicFocus: string) => void;
  /** Disable interaction while a resubmit is in flight. */
  disabled?: boolean;
}

/**
 * Transient interactive card rendered in place of an answer bubble when a
 * turn POST returns `status: "topic_clarification_needed"`. It is NOT a
 * persisted turn — nothing about it is saved server-side.
 *
 * Single-select (unlike the multi-chip pre-reader ClarificationCard): the
 * readers already enumerated the candidate branches, so picking exactly one
 * focus guarantees the synth can deliver. The topics render as a MUI
 * `RadioGroup`; an always-visible "Something else" free-text box lets the user
 * type an intent not among the topics (typing auto-selects "Something else").
 * A Confirm/Focus button (disabled until a topic is selected or the box has
 * text) calls `onChoose(<focus>)`; a Skip button calls `onChoose("")`.
 *
 * See issues/20260616-far-chat-post-reader-topic-focus_spec §4 / §9.
 */
const TopicFocusCard: React.FC<Props> = ({
  question,
  topics,
  originalQuery,
  onChoose,
  disabled = false,
}) => {
  const cleanTopics = (topics || []).filter((t) => !!t && t.trim());
  // The active radio value: a topic string, the SOMETHING_ELSE sentinel, or
  // null (nothing selected yet). Free-text lives in its own state so it
  // survives toggling radios.
  const [selected, setSelected] = useState<string | null>(null);
  const [freeText, setFreeText] = useState('');
  const trimmedFree = freeText.trim();

  const isSomethingElse = selected === SOMETHING_ELSE;
  // A real topic radio is the active choice (not the free-text escape hatch).
  const isTopicSelected = selected !== null && !isSomethingElse;

  // Confirm is enabled when a topic radio is selected, OR the always-visible
  // free-text box holds non-empty (trimmed) text. With no topic selected,
  // typing anything into the box is enough — it auto-selects "Something else".
  const confirmEnabled = isTopicSelected || trimmedFree.length > 0;

  // When a topic radio is the active choice it wins, even if the box still
  // holds stale text; otherwise the (trimmed) free text is the focus.
  const resolvedFocus = (): string => {
    if (isTopicSelected) return selected ?? '';
    return trimmedFree;
  };

  const handleFreeTextChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (disabled) return;
    setFreeText(e.target.value);
    // Typing into the box makes "Something else" the active choice, so a
    // previously selected topic radio is no longer the submitted focus.
    setSelected(SOMETHING_ELSE);
  };

  const handleConfirm = () => {
    // Guard against synthetic clicks bypassing the disabled prop (jsdom).
    if (disabled || !confirmEnabled) return;
    onChoose(resolvedFocus());
  };

  const handleSkip = () => {
    if (disabled) return;
    onChoose('');
  };

  return (
    <Box
      data-testid="topic-focus-card"
      data-role="assistant"
      sx={{
        alignSelf: 'flex-start',
        maxWidth: '85%',
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        bgcolor: (theme) => alpha(theme.palette.info.light, 0.45),
        color: 'text.primary',
        fontSize: '0.9rem',
        boxShadow: 1,
        border: '1px solid',
        borderColor: (theme) => alpha(theme.palette.info.main, 0.55),
        wordBreak: 'break-word',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <ChatBubbleDisclosure key={`${originalQuery}:${question}`} label="topic focus form" preview="" alwaysCollapsible defaultExpanded title={<Typography
        variant="body2"
        sx={{ fontWeight: 600 }}
        data-testid="topic-focus-question"
      >
        {question || 'Which topic would you like to focus on?'}
      </Typography>}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        data-testid="topic-focus-instruction"
      >
        Pick one topic to focus the answer on, or skip to answer everything.
      </Typography>

      <RadioGroup
        data-testid="topic-focus-options"
        value={selected ?? ''}
        onChange={(_e, val) => {
          if (disabled) return;
          setSelected(val);
        }}
        sx={{ gap: 0.5 }}
      >
        {cleanTopics.map((topic, idx) => {
          const isSel = selected === topic;
          return (
            <Box
              key={`${idx}-${topic}`}
              data-testid="topic-focus-option"
              data-selected={isSel ? 'true' : 'false'}
              sx={{
                borderRadius: 1,
                px: 0.75,
                py: 0.25,
                border: '1px solid transparent',
                ...(isSel ? SELECTED_SURFACE_SX : {}),
              }}
            >
              <FormControlLabel
                value={topic}
                disabled={disabled}
                control={<Radio size="small" />}
                label={topic}
                sx={{
                  m: 0,
                  alignItems: 'flex-start',
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.85rem',
                    py: 0.25,
                  },
                }}
              />
            </Box>
          );
        })}

        {/* "Something else" escape hatch — its free-text box is always
            visible below, so the user can type their own focus at any time. */}
        <Box
          data-testid="topic-focus-something-else"
          data-selected={isSomethingElse ? 'true' : 'false'}
          sx={{
            borderRadius: 1,
            px: 0.75,
            py: 0.25,
            border: '1px solid transparent',
            ...(isSomethingElse ? SELECTED_SURFACE_SX : {}),
          }}
        >
          <FormControlLabel
            value={SOMETHING_ELSE}
            disabled={disabled}
            control={<Radio size="small" />}
            label="Something else"
            sx={{
              m: 0,
              '& .MuiFormControlLabel-label': { fontSize: '0.85rem' },
            }}
          />
        </Box>
      </RadioGroup>

      {/* The free-text box is ALWAYS visible: typing here auto-selects
          "Something else" so the user can immediately type their own focus. */}
      <TextField
        data-testid="topic-focus-free-text"
        label="Something else"
        value={freeText}
        onChange={handleFreeTextChange}
        placeholder="Type your own focus…"
        size="small"
        fullWidth
        multiline
        maxRows={4}
        disabled={disabled}
        inputProps={
          {
            'aria-label': 'Type your own focus',
          } as React.InputHTMLAttributes<HTMLTextAreaElement>
        }
      />

      <Box sx={{ display: 'flex', gap: 1, mt: 0.25 }}>
        <Button
          data-testid="topic-focus-confirm"
          variant="contained"
          size="small"
          disabled={disabled || !confirmEnabled}
          onClick={handleConfirm}
        >
          Focus
        </Button>
        <Button
          data-testid="topic-focus-skip"
          variant="text"
          size="small"
          disabled={disabled}
          onClick={handleSkip}
        >
          Skip
        </Button>
      </Box>
      </Box>
      </ChatBubbleDisclosure>
    </Box>
  );
};

export default TopicFocusCard;
